import {
  CM_PER_INCH,
  POUNDS_PER_KILOGRAM,
  PROVISIONAL_FIT_CONFIGURATION,
  centimetresToFeetAndInches,
  feetAndInchesToCentimetres,
  kilogramsToPounds,
  poundsToKilograms,
  recommendVelortenFit,
  sizeDefinitionForValue,
  weightReferenceForSize,
  lengthReferenceForProfile,
} from './velorten-fit-system.js';

export {
  CM_PER_INCH,
  POUNDS_PER_KILOGRAM,
  PROVISIONAL_FIT_CONFIGURATION,
  centimetresToFeetAndInches,
  feetAndInchesToCentimetres,
  kilogramsToPounds,
  poundsToKilograms,
  recommendVelortenFit,
  weightReferenceForSize,
  lengthReferenceForProfile,
} from './velorten-fit-system.js';

export function triggerControlsDialog(trigger, dialog) {
  const controlledDialogId = trigger?.getAttribute?.('aria-controls')?.trim();
  return Boolean(controlledDialogId && dialog?.isConnected && controlledDialogId === dialog.id);
}

export const WHOLE_NUMBER_INPUT_ERROR = 'Use whole numbers only.';

export function isAsciiDigits(value, allowEmpty = false) {
  const raw = String(value ?? '');
  return allowEmpty ? /^[0-9]*$/.test(raw) : /^[0-9]+$/.test(raw);
}

export function acceptedPastedDigits(value) {
  const trimmed = String(value ?? '').trim();
  return isAsciiDigits(trimmed) ? trimmed : null;
}

export function boundaryFitMeaning(
  sizeValue,
  result,
  configuration = PROVISIONAL_FIT_CONFIGURATION
) {
  if (!result?.isBoundary || !sizeValue) return '';
  const selectedIndex = configuration.sizes.findIndex((size) => size.value === sizeValue);
  const primaryIndex = configuration.sizes.findIndex((size) => size.value === result.size);
  const alternativeIndex = configuration.sizes.findIndex((size) => size.value === result.alternativeSize);
  if (selectedIndex < 0 || primaryIndex < 0 || alternativeIndex < 0) return '';
  return selectedIndex === Math.min(primaryIndex, alternativeIndex) ? 'Closer fit' : 'More relaxed';
}

export function heightStateFromCentimetresInput(value) {
  const raw = String(value ?? '');
  if (!wholeNumber(raw)) {
    return { heightCm: null, height: { cm: raw, feet: '', inches: '' } };
  }
  const heightCm = Number(raw);
  const converted = centimetresToFeetAndInches(heightCm);
  return {
    heightCm,
    height: {
      cm: raw,
      feet: converted ? String(converted.feet) : '',
      inches: converted ? String(converted.inches) : '',
    },
  };
}

export function heightStateFromImperialInput(feetValue, inchesValue) {
  const feet = String(feetValue ?? '');
  const inches = String(inchesValue ?? '');
  const feetValid = wholeNumber(feet);
  const inchesValid = inches === '' || wholeNumber(inches);
  const numericInches = inches === '' ? 0 : Number(inches);
  if (!feetValid || !inchesValid || numericInches > 11) {
    return { heightCm: null, height: { cm: '', feet, inches } };
  }
  const heightCm = feetAndInchesToCentimetres(Number(feet), numericInches);
  return {
    heightCm,
    height: {
      cm: heightCm == null ? '' : clampDisplay(heightCm),
      feet,
      inches,
    },
  };
}

export function weightStateFromKilogramsInput(value) {
  const raw = String(value ?? '');
  if (!wholeNumber(raw)) return { weightKg: null, weight: { kg: raw, pounds: '' } };
  const weightKg = Number(raw);
  return {
    weightKg,
    weight: { kg: raw, pounds: clampDisplay(kilogramsToPounds(weightKg)) },
  };
}

export function weightStateFromPoundsInput(value) {
  const raw = String(value ?? '');
  if (!wholeNumber(raw)) return { weightKg: null, weight: { kg: '', pounds: raw } };
  const weightKg = poundsToKilograms(Number(raw));
  return {
    weightKg,
    weight: { kg: weightKg == null ? '' : clampDisplay(weightKg), pounds: raw },
  };
}

const wholeNumber = (value) => /^\d+$/.test(String(value || '').trim());
const clampDisplay = (value) => String(Math.round(value));

if (typeof globalThis.HTMLElement !== 'undefined' && typeof globalThis.customElements !== 'undefined') {
  class VelortenFindMyFit extends HTMLElement {
    #connected = false;
    #abortController = null;
    #config = {};
    #state = null;
    #lastTrigger = null;
    #restoreTarget = 'trigger';
    #announcementTimer = null;
    #wholeNumberErrorTimer = null;
    #pendingSizeGuideOpen = false;
    #compositionValues = new WeakMap();

    connectedCallback() {
      if (this.#connected) return;
      this.#connected = true;
      this.#abortController = new AbortController();
      this.#config = this.#readConfig();
      this.#state = this.#restoreState() || this.#emptyState();
      this.#bindEvents();
      this.#renderAll();
    }

    disconnectedCallback() {
      this.#connected = false;
      this.#abortController?.abort();
      this.#abortController = null;
      if (this.#announcementTimer) clearTimeout(this.#announcementTimer);
      if (this.#wholeNumberErrorTimer) clearTimeout(this.#wholeNumberErrorTimer);
      if (this.#dialog?.dataset.fallbackOpen === 'true') {
        this.#dialog.removeAttribute('open');
        delete this.#dialog.dataset.fallbackOpen;
      } else if (this.#dialog?.open) {
        this.#dialog.close('disconnected');
      }
      this.#unlockPage();
    }

    get #dialog() {
      return this.querySelector('dialog');
    }

    get #sectionRoot() {
      return this.closest('.shopify-section');
    }

    get #storageKey() {
      return `velorten_find_my_fit:v1:${this.dataset.productId || 'product'}`;
    }

    getRecommendationStatus() {
      const recommendation = this.#state?.recommendation;
      if (!recommendation) return null;

      const selectedSizeValue = this.#state.selectedResultSize || recommendation.size;
      const selectedSize = sizeDefinitionForValue(
        selectedSizeValue,
        PROVISIONAL_FIT_CONFIGURATION
      );
      const lengthProfile = String(recommendation.length || '').trim();
      const validLength = lengthReferenceForProfile(
        lengthProfile,
        PROVISIONAL_FIT_CONFIGURATION
      );

      if (!selectedSize?.displayName || !validLength) return null;

      return {
        size: selectedSize.displayName,
        lengthProfile,
      };
    }

    #readConfig() {
      const node = this.querySelector('[data-find-my-fit-config]');
      try {
        return JSON.parse(node?.textContent || '{}');
      } catch (error) {
        console.warn('[VELORTEN Find My Fit] Configuration could not be read.', error);
        return {};
      }
    }

    #emptyState() {
      return {
        step: 'height',
        heightCm: null,
        height: { cm: '', feet: '', inches: '' },
        weightKg: null,
        weight: { kg: '', pounds: '' },
        torsoShape: '',
        recommendation: null,
        selectedResultSize: '',
      };
    }

    #restoreState() {
      try {
        const parsed = JSON.parse(sessionStorage.getItem(this.#storageKey) || 'null');
        if (!parsed || typeof parsed !== 'object') return null;
        const state = { ...this.#emptyState(), ...parsed };
        state.height = { ...this.#emptyState().height, ...(parsed.height || {}) };
        state.weight = { ...this.#emptyState().weight, ...(parsed.weight || {}) };
        const heightValues = [state.height.cm, state.height.feet, state.height.inches];
        const weightValues = [state.weight.kg, state.weight.pounds];
        if (!heightValues.every((value) => isAsciiDigits(value, true))) {
          state.heightCm = null;
          state.height = { ...this.#emptyState().height };
        }
        if (!weightValues.every((value) => isAsciiDigits(value, true))) {
          state.weightKg = null;
          state.weight = { ...this.#emptyState().weight };
        }
        if (state.recommendation) {
          state.step = 'result';
        } else if (!['height', 'weight', 'torso'].includes(state.step)) {
          state.step = 'height';
        }
        return state;
      } catch {
        return null;
      }
    }

    #saveState() {
      try {
        sessionStorage.setItem(this.#storageKey, JSON.stringify(this.#state));
      } catch {
        // Session storage can be unavailable in strict privacy contexts.
      }
    }

    #bindEvents() {
      const signal = this.#abortController.signal;
      document.addEventListener('click', this.#handleDocumentClick, { signal });
      document.addEventListener('velorten:find-my-fit:open', this.#handleOpenRequest, { signal });
      this.addEventListener('click', this.#handleClick, { signal });
      this.addEventListener('beforeinput', this.#handleBeforeInput, { signal });
      this.addEventListener('keydown', this.#handleMeasurementKeydown, { signal });
      this.addEventListener('paste', this.#handlePaste, { signal });
      this.addEventListener('drop', this.#handleDrop, { signal });
      this.addEventListener('compositionstart', this.#handleCompositionStart, { signal });
      this.addEventListener('compositionend', this.#handleCompositionEnd, { signal });
      this.addEventListener('input', this.#handleInput, { signal });
      this.addEventListener('change', this.#handleChange, { signal });
      this.addEventListener('focusout', this.#handleFocusOut, { signal });

      this.#dialog?.addEventListener('cancel', this.#handleCancel, { signal });
      this.#dialog?.addEventListener('close', this.#handleClose, { signal });
      this.#dialog?.addEventListener('click', this.#handleBackdropClick, { signal });
      this.#dialog?.addEventListener('keydown', this.#handleDialogKeydown, { signal });
    }

    #handleDocumentClick = (event) => {
      if (
        this.#dialog?.dataset.fallbackOpen === 'true' &&
        event.target instanceof Node &&
        !this.#dialog.contains(event.target)
      ) {
        event.preventDefault();
        event.stopPropagation();
        this.#close('backdrop');
        return;
      }

      const trigger = event.target instanceof Element ? event.target.closest('[data-velorten-fit-trigger]') : null;
      if (!(trigger instanceof HTMLElement)) return;
      const dialog = this.#dialog;
      if (!triggerControlsDialog(trigger, dialog)) return;
      event.preventDefault();
      this.#lastTrigger = trigger;
      this.#restoreTarget = 'trigger';
      this.#open();
    };

    #handleClick = (event) => {
      const control = event.target instanceof Element ? event.target.closest('[data-fit-action]') : null;
      if (!(control instanceof HTMLElement)) return;
      const action = control.dataset.fitAction;

      if (action === 'close') this.#close('close');
      if (action === 'continue-height' && this.#heightValid()) this.#goTo('weight');
      if (action === 'continue-weight' && this.#weightValid()) this.#goTo('torso');
      if (action === 'continue-torso' && this.#torsoValid()) this.#showRecommendation();
      if (action === 'back-height') this.#goTo('height');
      if (action === 'back-weight') this.#goTo('weight');
      if (action === 'adjust') this.#goTo('height');
      if (action === 'restart') this.#restart();
      if (action === 'apply') this.#applyRecommendation();
      if (action === 'size-guide') this.#openSizeGuide();
    };

    #handleOpenRequest = (event) => {
      const detail = event?.detail || {};
      if (detail.sectionId && String(detail.sectionId) !== String(this.dataset.sectionId)) return;
      if (detail.productId && String(detail.productId) !== String(this.dataset.productId)) return;
      this.#lastTrigger = detail.returnTrigger instanceof HTMLElement
        ? detail.returnTrigger
        : detail.trigger instanceof HTMLElement
          ? detail.trigger
          : this.#matchingTrigger();
      this.#restoreTarget = 'trigger';
      this.#state.step = 'height';
      this.#renderAll();
      this.#saveState();
      this.#open();
    };

    #measurementTarget(event) {
      const input = event.target;
      return input instanceof HTMLInputElement && input.matches('[data-fit-measurement]')
        ? input
        : null;
    }

    #withinMaximumLength(input, value) {
      return input.maxLength < 0 || String(value).length <= input.maxLength;
    }

    #showWholeNumberInputError(input) {
      const step = input.closest('[data-fit-step]');
      const error = step?.querySelector('[data-fit-error]');
      if (error && (error.hidden || error.textContent !== WHOLE_NUMBER_INPUT_ERROR)) {
        this.#showError(step, WHOLE_NUMBER_INPUT_ERROR);
      }
      if (this.#wholeNumberErrorTimer) clearTimeout(this.#wholeNumberErrorTimer);
      this.#wholeNumberErrorTimer = setTimeout(() => {
        if (error?.textContent === WHOLE_NUMBER_INPUT_ERROR) this.#clearError(step);
        this.#wholeNumberErrorTimer = null;
      }, 3000);
    }

    #clearWholeNumberFeedback(step) {
      if (this.#wholeNumberErrorTimer) clearTimeout(this.#wholeNumberErrorTimer);
      this.#wholeNumberErrorTimer = null;
      this.#clearError(step);
    }

    #rejectCharacterInsertion(input) {
      input.dataset.velortenRejectedInsertion = 'true';
      this.#showWholeNumberInputError(input);
    }

    #restoreLastValidInput(input, fallback = input.dataset.velortenValidValue || '') {
      input.value = fallback;
      input.dataset.velortenValidValue = fallback;
      const cursor = fallback.length;
      input.setSelectionRange?.(cursor, cursor);
      this.#rejectCharacterInsertion(input);
    }

    #handleMeasurementKeydown = (event) => {
      const input = this.#measurementTarget(event);
      if (!input || event.isComposing || event.keyCode === 229) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.key === 'Backspace' || event.key === 'Delete') {
        delete input.dataset.velortenRejectedInsertion;
        return;
      }
      if (isAsciiDigits(event.key) && input.dataset.velortenRejectedInsertion === 'true') {
        event.preventDefault();
        delete input.dataset.velortenRejectedInsertion;
        this.#clearWholeNumberFeedback(input.closest('[data-fit-step]'));
        return;
      }
      if (event.key.length === 1 && !isAsciiDigits(event.key)) {
        event.preventDefault();
        this.#rejectCharacterInsertion(input);
      }
    };

    #handleBeforeInput = (event) => {
      const input = this.#measurementTarget(event);
      if (!input || event.isComposing || input.dataset.velortenComposing === 'true') return;
      if (!String(event.inputType || '').startsWith('insert') || event.data == null) return;

      if (isAsciiDigits(event.data) && input.dataset.velortenRejectedInsertion === 'true') {
        event.preventDefault();
        delete input.dataset.velortenRejectedInsertion;
        this.#clearWholeNumberFeedback(input.closest('[data-fit-step]'));
        return;
      }

      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      const nextValue = `${input.value.slice(0, start)}${event.data}${input.value.slice(end)}`;
      if (!isAsciiDigits(nextValue, true) || !this.#withinMaximumLength(input, nextValue)) {
        event.preventDefault();
        this.#rejectCharacterInsertion(input);
      }
    };

    #insertDigits(input, digits) {
      if (input.dataset.velortenRejectedInsertion === 'true') {
        delete input.dataset.velortenRejectedInsertion;
        this.#clearWholeNumberFeedback(input.closest('[data-fit-step]'));
        return;
      }
      const start = input.selectionStart ?? input.value.length;
      const end = input.selectionEnd ?? start;
      const nextValue = `${input.value.slice(0, start)}${digits}${input.value.slice(end)}`;
      if (!this.#withinMaximumLength(input, nextValue)) {
        this.#showWholeNumberInputError(input);
        return;
      }
      delete input.dataset.velortenRejectedInsertion;
      this.#clearWholeNumberFeedback(input.closest('[data-fit-step]'));
      input.focus({ preventScroll: true });
      input.setRangeText(digits, start, end, 'end');
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    #handlePaste = (event) => {
      const input = this.#measurementTarget(event);
      if (!input) return;
      event.preventDefault();
      const digits = acceptedPastedDigits(event.clipboardData?.getData('text'));
      if (digits == null) {
        this.#rejectCharacterInsertion(input);
        return;
      }
      this.#insertDigits(input, digits);
    };

    #handleDrop = (event) => {
      const input = this.#measurementTarget(event);
      if (!input) return;
      event.preventDefault();
      const digits = acceptedPastedDigits(event.dataTransfer?.getData('text'));
      if (digits == null) {
        this.#rejectCharacterInsertion(input);
        return;
      }
      this.#insertDigits(input, digits);
    };

    #handleCompositionStart = (event) => {
      const input = this.#measurementTarget(event);
      if (!input) return;
      this.#compositionValues.set(input, input.dataset.velortenValidValue || input.value);
      input.dataset.velortenComposing = 'true';
    };

    #handleCompositionEnd = (event) => {
      const input = this.#measurementTarget(event);
      if (!input) return;
      const fallback = this.#compositionValues.get(input) ?? input.dataset.velortenValidValue ?? '';
      this.#compositionValues.delete(input);
      delete input.dataset.velortenComposing;
      if (!isAsciiDigits(input.value, true) || !this.#withinMaximumLength(input, input.value)) {
        this.#restoreLastValidInput(input, fallback);
        return;
      }
      input.dataset.velortenValidValue = input.value;
      delete input.dataset.velortenRejectedInsertion;
      this.#processMeasurementInput(input);
    };

    #handleInput = (event) => {
      const input = this.#measurementTarget(event);
      if (!input || event.isComposing || input.dataset.velortenComposing === 'true') return;
      if (!isAsciiDigits(input.value, true) || !this.#withinMaximumLength(input, input.value)) {
        this.#restoreLastValidInput(input);
        return;
      }
      input.dataset.velortenValidValue = input.value;
      delete input.dataset.velortenRejectedInsertion;
      this.#processMeasurementInput(input);
    };

    #processMeasurementInput(input) {
      if (input.dataset.fitMeasurement === 'height-cm') this.#updateHeightFromCentimetres(input.value);
      if (input.dataset.fitMeasurement === 'height-feet') this.#updateHeightFromImperial('feet', input.value);
      if (input.dataset.fitMeasurement === 'height-inches') this.#updateHeightFromImperial('inches', input.value);
      if (input.dataset.fitMeasurement === 'weight-kg') this.#updateWeightFromKilograms(input.value);
      if (input.dataset.fitMeasurement === 'weight-pounds') this.#updateWeightFromPounds(input.value);

      this.#state.recommendation = null;
      this.#state.selectedResultSize = '';
      this.#clearWholeNumberFeedback(input.closest('[data-fit-step]'));
      this.#updateContinueStates();
      this.#saveState();
    }

    #handleChange = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement)) return;

      if (input.name === `velorten-fit-torso-${this.dataset.sectionId}`) {
        this.#state.torsoShape = input.value;
        this.#state.recommendation = null;
        this.#state.selectedResultSize = '';
        this.#updateContinueStates();
        this.#saveState();
      }

      if (input.name === `velorten-fit-result-size-${this.dataset.sectionId}`) {
        this.#state.selectedResultSize = input.value;
        this.#renderRecommendation();
        this.#saveState();
        this.#announceResultSize(input.value);
      }
    };

    #handleFocusOut = (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || !input.matches('[data-fit-measurement]')) return;
      delete input.dataset.velortenRejectedInsertion;
      const step = input.closest('[data-fit-step]');
      if (this.#wholeNumberErrorTimer) clearTimeout(this.#wholeNumberErrorTimer);
      this.#wholeNumberErrorTimer = null;
      const valid = input.dataset.fitMeasurement.startsWith('height') ? this.#heightValid() : this.#weightValid();
      if (!valid) this.#showError(step, this.#measurementError(input.dataset.fitMeasurement));
      else this.#clearError(step);
      this.#scheduleAnnouncement();
    };

    #handleCancel = (event) => {
      event.preventDefault();
      this.#close('escape');
    };

    #handleClose = () => {
      this.#unlockPage();
      this.#saveState();
      if (this.#pendingSizeGuideOpen) {
        this.#pendingSizeGuideOpen = false;
        document.dispatchEvent(
          new CustomEvent('velorten:size-guide:open', {
            detail: {
              productId: this.dataset.productId,
              sectionId: this.dataset.sectionId,
              source: 'find-my-fit',
              returnTrigger: this.#lastTrigger,
            },
          })
        );
        return;
      }
      requestAnimationFrame(() => {
        if (this.#restoreTarget === 'selection') this.#focusProductSelection();
        else this.#matchingTrigger()?.focus({ preventScroll: true });
      });
    };

    #handleBackdropClick = (event) => {
      if (event.target === this.#dialog) this.#close('backdrop');
    };

    #handleDialogKeydown = (event) => {
      if (event.key === 'Escape' && this.#dialog?.dataset.fallbackOpen === 'true') {
        event.preventDefault();
        this.#close('escape');
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = this.#focusableElements();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable.at(-1);
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    #focusableElements() {
      return Array.from(
        this.#dialog?.querySelectorAll(
          'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((element) => element instanceof HTMLElement && !element.closest('[hidden]'));
    }

    #matchingTrigger() {
      if (this.#lastTrigger?.isConnected) return this.#lastTrigger;
      const dialogId = this.#dialog?.id;
      if (!dialogId) return null;
      return (
        Array.from(document.querySelectorAll('[data-velorten-fit-trigger][aria-controls]')).find(
          (trigger) => trigger.getAttribute('aria-controls') === dialogId
        ) || null
      );
    }

    #open() {
      const dialog = this.#dialog;
      if (!dialog?.isConnected || dialog.open) return;
      this.#renderAll();
      this.#lockPage();
      if (typeof dialog.showModal === 'function') {
        try {
          dialog.showModal();
        } catch (error) {
          this.#unlockPage();
          console.warn('[VELORTEN Find My Fit] Dialog could not be opened.', error);
          return;
        }
      } else {
        dialog.setAttribute('open', '');
        dialog.dataset.fallbackOpen = 'true';
      }
      requestAnimationFrame(() => this.#focusCurrentStep());
    }

    #close(returnValue = '') {
      const dialog = this.#dialog;
      if (!dialog) return;
      if (dialog.dataset.fallbackOpen === 'true') {
        dialog.removeAttribute('open');
        delete dialog.dataset.fallbackOpen;
        this.#handleClose();
        return;
      }
      if (!dialog.open) return;
      dialog.close(returnValue);
    }

    #lockPage() {
      document.documentElement.classList.add('velorten-find-my-fit-is-open');
    }

    #unlockPage() {
      if (!document.querySelector('velorten-find-my-fit dialog[open]')) {
        document.documentElement.classList.remove('velorten-find-my-fit-is-open');
      }
    }

    #goTo(step) {
      this.#state.step = step;
      this.#renderAll();
      this.#saveState();
      requestAnimationFrame(() => this.#focusCurrentStep());
    }

    #restart() {
      try {
        sessionStorage.removeItem(this.#storageKey);
      } catch {
        // No persistent fallback is used.
      }
      this.#state = this.#emptyState();
      this.#renderAll();
      requestAnimationFrame(() => this.#focusCurrentStep());
    }

    #openSizeGuide() {
      this.#pendingSizeGuideOpen = true;
      this.#close('size-guide');
    }

    #heightValid() {
      const range = PROVISIONAL_FIT_CONFIGURATION.measurementRanges.heightCm;
      return Number.isFinite(this.#state.heightCm) && this.#state.heightCm >= range.min && this.#state.heightCm <= range.max;
    }

    #weightValid() {
      const range = PROVISIONAL_FIT_CONFIGURATION.measurementRanges.weightKg;
      return Number.isFinite(this.#state.weightKg) && this.#state.weightKg >= range.min && this.#state.weightKg <= range.max;
    }

    #torsoValid() {
      return ['lean', 'athletic', 'broad'].includes(this.#state.torsoShape);
    }

    #updateHeightFromCentimetres(value) {
      const next = heightStateFromCentimetresInput(value);
      this.#state.heightCm = next.heightCm;
      this.#state.height = next.height;
      this.#syncHeightInputs('height-cm');
    }

    #updateHeightFromImperial(field, value) {
      const feetValue = field === 'feet' ? value : this.#state.height.feet;
      const inchesValue = field === 'inches' ? value : this.#state.height.inches;
      const next = heightStateFromImperialInput(feetValue, inchesValue);
      this.#state.heightCm = next.heightCm;
      this.#state.height = next.height;
      this.#syncHeightInputs(`height-${field}`);
    }

    #updateWeightFromKilograms(value) {
      const next = weightStateFromKilogramsInput(value);
      this.#state.weightKg = next.weightKg;
      this.#state.weight = next.weight;
      this.#syncWeightInputs('weight-kg');
    }

    #updateWeightFromPounds(value) {
      const next = weightStateFromPoundsInput(value);
      this.#state.weightKg = next.weightKg;
      this.#state.weight = next.weight;
      this.#syncWeightInputs('weight-pounds');
    }

    #syncHeightInputs(activeField = '') {
      const mapping = {
        'height-cm': this.#state.height.cm,
        'height-feet': this.#state.height.feet,
        'height-inches': this.#state.height.inches,
      };
      Object.entries(mapping).forEach(([name, value]) => {
        if (name === activeField) return;
        const input = this.querySelector(`[data-fit-measurement="${name}"]`);
        if (input instanceof HTMLInputElement) {
          input.value = value;
          input.dataset.velortenValidValue = value;
        }
      });
    }

    #syncWeightInputs(activeField = '') {
      const mapping = {
        'weight-kg': this.#state.weight.kg,
        'weight-pounds': this.#state.weight.pounds,
      };
      Object.entries(mapping).forEach(([name, value]) => {
        if (name === activeField) return;
        const input = this.querySelector(`[data-fit-measurement="${name}"]`);
        if (input instanceof HTMLInputElement) {
          input.value = value;
          input.dataset.velortenValidValue = value;
        }
      });
    }

    #measurementError(measurement) {
      if (measurement.startsWith('height')) return 'Enter a height between 145 and 220 cm, or the equivalent in feet and inches.';
      return 'Enter a weight between 45 and 200 kg, or the equivalent in pounds.';
    }

    #showError(step, message) {
      const error = step?.querySelector('[data-fit-error]');
      if (error) {
        error.textContent = message;
        error.hidden = false;
      }
    }

    #clearError(step) {
      const error = step?.querySelector('[data-fit-error]');
      if (error) {
        error.textContent = '';
        error.hidden = true;
      }
    }

    #scheduleAnnouncement() {
      if (this.#announcementTimer) clearTimeout(this.#announcementTimer);
      this.#announcementTimer = setTimeout(() => {
        const live = this.querySelector('[data-fit-live]');
        if (!live) return;
        if (this.#state.step === 'height' && this.#heightValid()) {
          live.textContent = `${this.#state.height.cm} centimetres equals ${this.#state.height.feet} feet ${this.#state.height.inches || '0'} inches.`;
        } else if (this.#state.step === 'weight' && this.#weightValid()) {
          live.textContent = `${this.#state.weight.kg} kilograms equals ${this.#state.weight.pounds} pounds.`;
        }
      }, 350);
    }

    #showRecommendation() {
      const recommendation = recommendVelortenFit({
        heightCm: this.#state.heightCm,
        weightKg: this.#state.weightKg,
        torsoShape: this.#state.torsoShape,
      });
      if (!recommendation) return;
      this.#state.recommendation = recommendation;
      this.#state.selectedResultSize = recommendation.size;
      this.#state.step = 'result';
      this.#renderAll();
      this.#saveState();
      requestAnimationFrame(() => this.#focusCurrentStep());
    }

    async #applyRecommendation() {
      const recommendation = this.#state.recommendation;
      const selectedSize = this.#state.selectedResultSize || recommendation?.size;
      if (!recommendation || !selectedSize) return;

      if (!this.#sectionRoot?.querySelector('variant-picker')) {
        this.#restoreTarget = 'trigger';
        this.#close('completed');
        return;
      }

      const settlement = this.#waitForNativeSelection();
      document.dispatchEvent(
        new CustomEvent('velorten:fit:confirmed', {
          detail: {
            productId: this.dataset.productId,
            sectionId: this.dataset.sectionId,
            size: selectedSize,
            length: recommendation.length,
            source: 'find-my-fit',
          },
        })
      );
      await settlement;
      this.dispatchEvent(
        new CustomEvent('velorten:fit-applied', {
          bubbles: true,
          detail: {
            size: selectedSize,
            lengthProfile: recommendation.length,
            source: 'find-my-fit',
          },
        })
      );
      this.#restoreTarget = 'selection';
      this.#close('applied');
    }

    #waitForNativeSelection() {
      return new Promise((resolve) => {
        const root = this.#sectionRoot;
        let settled = false;
        let timer = null;
        const finish = () => {
          if (settled) return;
          settled = true;
          if (timer) clearTimeout(timer);
          root?.removeEventListener('shopify:product:select', handleSelect);
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        };
        const handleSelect = (event) => Promise.resolve(event.promise).finally(finish);
        root?.addEventListener('shopify:product:select', handleSelect, { once: true });
        timer = setTimeout(finish, 650);
      });
    }

    #focusProductSelection() {
      const picker = this.#sectionRoot?.querySelector('variant-picker');
      const size = picker?.querySelector('fieldset[data-velorten-option="size"]');
      const target = size?.querySelector('input:not(:disabled), select:not(:disabled), button:not(:disabled)');
      picker?.scrollIntoView({
        block: 'nearest',
        inline: 'nearest',
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      if (target instanceof HTMLElement) target.focus({ preventScroll: true });
    }

    #renderAll() {
      this.querySelectorAll('[data-fit-step]').forEach((step) => {
        step.hidden = step.dataset.fitStep !== this.#state.step;
      });

      this.#dialog?.setAttribute(
        'aria-labelledby',
        `${this.id}-${this.#state.step === 'result' ? 'result' : this.#state.step}-title`
      );

      this.#syncStoredInputs();
      this.#renderTorso();
      this.#renderRecommendation();
      this.#updateContinueStates();
    }

    #syncStoredInputs() {
      const values = {
        'height-cm': this.#state.height.cm,
        'height-feet': this.#state.height.feet,
        'height-inches': this.#state.height.inches,
        'weight-kg': this.#state.weight.kg,
        'weight-pounds': this.#state.weight.pounds,
      };
      Object.entries(values).forEach(([name, value]) => {
        const input = this.querySelector(`[data-fit-measurement="${name}"]`);
        if (!(input instanceof HTMLInputElement)) return;
        if (document.activeElement !== input) input.value = value;
        if (input.dataset.velortenComposing !== 'true') {
          input.dataset.velortenValidValue = input.value;
        }
      });
    }

    #renderTorso() {
      this.querySelectorAll(`input[name="velorten-fit-torso-${this.dataset.sectionId}"]`).forEach((input) => {
        if (input instanceof HTMLInputElement) input.checked = input.value === this.#state.torsoShape;
      });
    }

    #renderRecommendation() {
      const result = this.#state.recommendation;
      const resultStep = this.querySelector('[data-fit-step="result"]');
      const resultName = this.querySelector('[data-fit-result-name]');
      const boundary = this.querySelector('[data-fit-boundary]');
      const details = this.querySelector('[data-fit-result-details]');
      const why = this.querySelector('[data-fit-result-why]');
      const references = this.querySelector('[data-fit-result-references]');
      const applyButton = this.querySelector('[data-fit-action="apply"]');
      if (!result) {
        resultStep?.classList.remove('velorten-find-my-fit__step--between-sizes');
        if (resultName) resultName.textContent = '';
        if (boundary) boundary.hidden = true;
        this.querySelectorAll('[data-fit-result-size]').forEach((input) => {
          if (input instanceof HTMLInputElement) {
            input.checked = false;
            input.value = '';
          }
        });
        details?.replaceChildren();
        why?.replaceChildren();
        references?.replaceChildren();
        if (applyButton) {
          applyButton.textContent = this.dataset.productContext === 'true'
            ? 'Apply recommended fit'
            : 'Done';
        }
        return;
      }
      const displayedSize = sizeDefinitionForValue(
        this.#state.selectedResultSize || result.size,
        PROVISIONAL_FIT_CONFIGURATION
      );
      const displayedSizeName = displayedSize?.displayName || result.sizeDisplayName;
      const displayedFitMeaning = boundaryFitMeaning(displayedSize?.value, result);
      resultStep?.classList.toggle('velorten-find-my-fit__step--between-sizes', result.isBoundary);
      if (resultName) resultName.textContent = `${displayedSizeName} · ${result.length}`;
      if (applyButton) {
        applyButton.textContent = this.dataset.productContext === 'true'
          ? `Apply ${displayedSizeName} · ${result.length}`
          : 'Done';
      }

      if (boundary) boundary.hidden = !result.isBoundary;
      if (result.isBoundary) {
        this.querySelectorAll('[data-fit-result-size]').forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          const value = input.dataset.fitResultSize === 'primary' ? result.size : result.alternativeSize;
          const name = input.dataset.fitResultSize === 'primary' ? result.sizeDisplayName : result.alternativeDisplayName;
          const fitMeaning = boundaryFitMeaning(value, result);
          input.value = value;
          input.checked = value === (this.#state.selectedResultSize || result.size);
          const label = input.closest('label')?.querySelector('[data-fit-result-size-label]');
          if (label) label.textContent = `${name}: ${fitMeaning}`;
        });
      }

      if (details) {
        const shapeLabel = this.#config.torso?.[this.#state.torsoShape]?.label || this.#state.torsoShape;
        details.replaceChildren(
          this.#detailRow('Height', `${this.#state.height.cm} cm · ${this.#state.height.feet} ft ${this.#state.height.inches || '0'} in`),
          this.#detailRow('Weight', `${this.#state.weight.kg} kg · ${this.#state.weight.pounds} lb`),
          this.#detailRow('Torso shape', shapeLabel)
        );
      }

      if (why) {
        const sizeExplanation = result.isBoundary
          ? `${displayedFitMeaning} within your adjacent-size recommendation.`
          : 'Best matched to your weight and torso profile.';
        why.replaceChildren(
          this.#detailRow(displayedSizeName, sizeExplanation),
          this.#detailRow(`${result.length} Length`, 'Recommended for your height.')
        );
      }

      if (references) {
        const weightReference = weightReferenceForSize(displayedSize?.value, PROVISIONAL_FIT_CONFIGURATION);
        const lengthReference = lengthReferenceForProfile(result.length, PROVISIONAL_FIT_CONFIGURATION);
        references.replaceChildren(
          ...[weightReference?.text, lengthReference?.text]
            .filter(Boolean)
            .map((text) => this.#referenceLine(text))
        );
      }
    }

    #announceResultSize(sizeValue) {
      const result = this.#state.recommendation;
      const size = sizeDefinitionForValue(sizeValue, PROVISIONAL_FIT_CONFIGURATION);
      const fitMeaning = boundaryFitMeaning(sizeValue, result);
      const live = this.querySelector('[data-fit-live]');
      if (!result?.isBoundary || !size || !fitMeaning || !live) return;
      if (this.#announcementTimer) clearTimeout(this.#announcementTimer);
      live.textContent = `${size.displayName} selected. ${fitMeaning}. ${result.length} Length remains selected.`;
    }

    #detailRow(term, description) {
      const wrapper = document.createElement('div');
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = term;
      dd.textContent = description;
      wrapper.append(dt, dd);
      return wrapper;
    }

    #referenceLine(text) {
      const paragraph = document.createElement('p');
      paragraph.textContent = text;
      return paragraph;
    }

    #updateContinueStates() {
      const states = {
        'continue-height': this.#heightValid(),
        'continue-weight': this.#weightValid(),
        'continue-torso': this.#torsoValid(),
      };
      Object.entries(states).forEach(([action, enabled]) => {
        const button = this.querySelector(`[data-fit-action="${action}"]`);
        if (button instanceof HTMLButtonElement) {
          button.disabled = !enabled;
          button.setAttribute('aria-disabled', enabled ? 'false' : 'true');
        }
      });
    }

    #focusCurrentStep() {
      const step = this.querySelector(`[data-fit-step="${this.#state.step}"]`);
      const heading = step?.querySelector('h2');
      if (heading instanceof HTMLElement) heading.focus({ preventScroll: true });
    }
  }

  if (!customElements.get('velorten-find-my-fit')) {
    customElements.define('velorten-find-my-fit', VelortenFindMyFit);
  }
}
